import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { ApplicationCode, Paths } from "configs";
import { useAppNavigate } from "contexts";
import qs from "query-string";
import { UserProfile } from "services";
import {
  clearAccessToken,
  clearRefreshToken,
  loadLocalStorage,
  removeSessionStorage,
  saveLocalStorage,
  saveSessionStorage,
  ThemeUtils,
} from "utils";

import { initUserSessionAction } from "./thunks";

export interface AuthReducerState {
  status: "AUTH" | "UNAUTH";
  userProfile: UserProfile | null;
  userProfileInitializing: boolean;
  userProfileFetching: boolean;
  organizations?: UserProfile["organizations"];
  currentOrganization?: UserProfile["organizations"][0];
}

const initialState: AuthReducerState = {
  status: "UNAUTH",
  userProfile: null,
  userProfileInitializing: false,
  userProfileFetching: false,
};

export const authSlice = createSlice({
  name: "Auth",
  initialState,
  reducers: {
    logout: (state) => {
      clearAccessToken();
      clearRefreshToken();
      removeSessionStorage("Organization");
      ThemeUtils.clearTheme();
      state.status = "UNAUTH";
      state.userProfile = null;
      state.userProfileInitializing = false;
      state.userProfileFetching = false;
    },
    changeOrg: (state, action) => {
      state.currentOrganization = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(initUserSessionAction.pending, (state) => {
      state.userProfileInitializing = true;
    });
    builder.addCase(
      initUserSessionAction.fulfilled,
      (state, action: PayloadAction<UserProfile>) => {
        const { push } = useAppNavigate();
        const organizations = action.payload?.organizations?.filter(
          (org) => org?.application?.code === ApplicationCode && org.employeeId
        );

        const searchPathNameURL = qs.parse(window.location.pathname);
        const searchHrefURL = qs.parse(window.location.href);

        const orgFromLocal =
          searchPathNameURL?.orgId ||
          searchHrefURL?.orgId ||
          loadLocalStorage("org");
        const orgSelected =
          organizations?.find(
            (org) => Number(org.id) === Number(orgFromLocal)
          ) || organizations?.[0];

        if (organizations?.length) {
          saveSessionStorage("Organization", orgSelected);
          saveLocalStorage("org", orgSelected.id);
          // eslint-disable-next-line prefer-destructuring
          state.currentOrganization = orgSelected;
        } else {
          push({
            to: Paths.Forbidden,
          });
        }

        ThemeUtils.initTheme(
          orgSelected?.settings?.theme,
          orgSelected?.settings?.themeName
        );

        state.organizations = organizations;
        state.userProfileInitializing = false;
        state.status = "AUTH";
        state.userProfile = action.payload;
      }
    );
    builder.addCase(initUserSessionAction.rejected, (state) => {
      state.userProfileInitializing = false;
      state.status = "UNAUTH";
      state.userProfile = null;
    });
  },
});
